import React, { useEffect, useRef, useState } from "react";
import { track } from "../../utils/analytics";
import { getOnboarding, saveOnboardingAnswers } from "../../utils/attribution";
import { QUESTIONS } from "./onboardingContent";
import OnboardingQuestion from "./OnboardingQuestion";
import OnboardingShowcase from "./OnboardingShowcase";
import OnboardingDemo from "./OnboardingDemo";
import OnboardingTopicDemo from "./OnboardingTopicDemo";
import OnboardingCallSim from "./OnboardingCallSim";
import OnboardingPdfSim from "./OnboardingPdfSim";
import OnboardingHookQuiz from "./OnboardingHookQuiz";
import OnboardingCta from "./OnboardingCta";

/**
 * OnboardingPage — the public guided funnel at /start for social-media
 * visitors. Mounted from index.js BEFORE App (like /irab), so no auth gate
 * or URL-param handling runs here.
 *
 * Steps: hook → three questions → tailored showcase → live iʿrāb demo → trial
 * CTA. Answers persist to localStorage on every advance (survives refresh and
 * bounce); SignupPage later carries them into the signup payload.
 */
const STEPS = ["hook", "motivation", "script_level", "daily_minutes", "showcase", "demo", "topic", "cta"];

export default function OnboardingPage() {
  const [answers, setAnswers] = useState(() => {
    const d = getOnboarding();
    return {
      motivation: d.motivation || null,
      script_level: d.script_level || null,
      daily_minutes: d.daily_minutes || null,
    };
  });
  // Resume where a returning visitor left off, based on what's answered.
  const [step, setStep] = useState(() => {
    const d = getOnboarding();
    if (d.daily_minutes) return "showcase";
    if (d.script_level) return "daily_minutes";
    if (d.motivation) return "script_level";
    return "hook";
  });
  const startTracked = useRef(false);

  useEffect(() => {
    document.title = "Start learning Quranic Arabic — Murājaʿa";
    if (startTracked.current) return; // StrictMode double-effect guard
    startTracked.current = true;
    const { utm_source: source, utm_campaign: campaign } = getOnboarding();
    track("onboarding-start", { source: source || "direct", campaign: campaign || "none" });
  }, []);

  const stepIndex = STEPS.indexOf(step);

  const answerQuestion = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    saveOnboardingAnswers({ [key]: value });
    track("onboarding-question", { step: key, answer: String(value) });
    setStep(STEPS[STEPS.indexOf(key) + 1]);
  };

  const goBack = () => {
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1]);
  };

  const advance = (to) => {
    if (to === "showcase") track("onboarding-showcase", { variant: answers.motivation || "quran" });
    if (to === "topic") track("onboarding-topic-view", {});
    setStep(to);
  };

  const question = QUESTIONS.find((q) => q.key === step);

  return (
    <div className="dark-scope min-h-screen flex flex-col" style={{ background: "var(--ink)" }}>
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 max-w-lg w-full mx-auto">
        <a href="https://muraja.app" className="flex items-baseline gap-2 no-underline">
          <span className="font-amiri text-2xl text-dt-gold-bright">مراجعة</span>
          <span className="text-xs text-dt-parchment-dim tracking-wide">murājaʿa</span>
        </a>
        {stepIndex > 0 && step !== "cta" && (
          <button
            type="button"
            onClick={goBack}
            className="text-sm text-dt-parchment-dim hover:text-dt-parchment transition-colors"
          >
            ← Back
          </button>
        )}
      </header>

      {/* Progress dots */}
      <div className="flex justify-center gap-1.5 pb-6" aria-hidden="true">
        {STEPS.map((s, i) => (
          <span
            key={s}
            className="w-1.5 h-1.5 rounded-full transition-colors"
            style={{ background: i <= stepIndex ? "var(--gold)" : "var(--panel-line)" }}
          />
        ))}
      </div>

      {/* Step content */}
      <main className="flex-1 w-full max-w-lg mx-auto px-5 pb-10">
        {step === "hook" && <OnboardingHookQuiz onDone={() => advance("motivation")} />}
        {question && (
          <OnboardingQuestion
            question={question}
            value={answers[question.key]}
            onSelect={(value) => answerQuestion(question.key, value)}
          />
        )}
        {step === "showcase" && (
          <OnboardingShowcase motivation={answers.motivation} onContinue={() => advance("demo")} />
        )}
        {step === "demo" && <OnboardingDemo onContinue={() => advance("topic")} />}
        {/* One flagship-feature taste per motivation, same funnel length:
            speaking → practice-call sim, classical texts → PDF-reader sim,
            quran/salah (and default) → the lesson word-tap taste. */}
        {step === "topic" &&
          (answers.motivation === "speaking" ? (
            <OnboardingCallSim onContinue={() => advance("cta")} />
          ) : answers.motivation === "classical_texts" ? (
            <OnboardingPdfSim onContinue={() => advance("cta")} />
          ) : (
            <OnboardingTopicDemo onContinue={() => advance("cta")} />
          ))}
        {step === "cta" && (
          <OnboardingCta motivation={answers.motivation} dailyMinutes={answers.daily_minutes} />
        )}
      </main>

      {/* Footer */}
      <footer className="px-5 py-4 text-center text-xs text-dt-parchment-dim space-x-3">
        <a href="https://muraja.app" className="hover:text-dt-parchment">muraja.app</a>
        <a href="https://muraja.app/privacy.html" className="hover:text-dt-parchment">Privacy</a>
        <a href="https://muraja.app/terms.html" className="hover:text-dt-parchment">Terms</a>
      </footer>
    </div>
  );
}
